const DB_NAME = "arna-recordings";
const DB_VERSION = 1;
const STORE_NAME = "recordings";

export const RECORDING_RETENTION_DAYS = 30;
const RETENTION_STORAGE_KEY = "arna-recording-retention-days";

export function getRetentionDays(): number {
  const stored = localStorage.getItem(RETENTION_STORAGE_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  return RECORDING_RETENTION_DAYS;
}

export function setRetentionDays(days: number): void {
  localStorage.setItem(RETENTION_STORAGE_KEY, String(days));
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "consultationId",
        });
        store.createIndex("savedAt", "savedAt");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveRecording(
  consultationId: string,
  blob: Blob,
  mimeType: string,
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({
      consultationId,
      blob,
      mimeType,
      savedAt: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getRecording(
  consultationId: string,
): Promise<{ blob: Blob; mimeType: string; savedAt: number } | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(consultationId);
    req.onsuccess = () => resolve((req.result as { blob: Blob; mimeType: string; savedAt: number } | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function cleanupExpiredRecordings(
  retentionDays = RECORDING_RETENTION_DAYS,
): Promise<void> {
  const db = await openDB();
  const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const index = tx.objectStore(STORE_NAME).index("savedAt");
    const range = IDBKeyRange.upperBound(cutoff);
    const req = index.openCursor(range);
    req.onsuccess = (e) => {
      const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
